import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import "./DailyPurchases.css";

const SuiviDemandesAchats = ({ onNewDemandClick }) => {
  const currentUser = useSelector((state) => state.AuthReducer?.user);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);

  const isAdmin =
    currentUser?.role === "admin" || currentUser?.role === "useradmin";
  const isController =
    currentUser?.role === "controleur" ||
    (currentUser?.fonction && currentUser.fonction.toLowerCase().includes("control"));
  const isControllerOrAdmin = isAdmin || isController;

  const userLocName =
    (currentUser?.localisation && typeof currentUser.localisation === "object" && currentUser.localisation.name) ||
    (typeof currentUser?.localisation === "string" && currentUser.localisation) ||
    localStorage.getItem("userLocation") ||
    localStorage.getItem("localisation") ||
    "";

  const fetchMyRequests = async () => {
    setLoading(true);
    try {
      const params = {};
      if (!isControllerOrAdmin && userLocName) {
        params.locationName = userLocName;
      }
      const res = await axios.get("/api/daily-purchases/requests", { params });
      let list = res.data || [];
      if (!isControllerOrAdmin && userLocName) {
        list = list.filter(
          (r) =>
            r.locationName?.toLowerCase() === userLocName.toLowerCase() ||
            (r.requestedBy && currentUser?._id && String(r.requestedBy) === String(currentUser._id))
        );
      }
      setRequests(list);
    } catch (err) {
      console.error("Erreur chargement suivi des demandes:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintRequest = (reqToPrint) => {
    if (!reqToPrint) return;
    const items = reqToPrint.items || [];
    const dateFormatted =
      reqToPrint.createdAt || reqToPrint.requestDate
        ? new Date(reqToPrint.createdAt || reqToPrint.requestDate).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
          })
        : new Date().toLocaleDateString("fr-FR");

    const totalAmount = (
      Number(reqToPrint.totalActualAmount) ||
      items.reduce(
        (s, it) =>
          s +
          (Number(it.actualTotalPrice) ||
            Number(it.estimatedTotalPrice) ||
            (Number(it.purchasedQuantity || it.requestedQuantity) *
              Number(it.actualUnitPrice || it.estimatedUnitPrice)) ||
            0),
        0
      )
    ).toFixed(3);

    const isValidated = reqToPrint.status === "valide_controleur";

    const rowsHtml = items
      .map(
        (it, idx) => `
        <tr>
          <td style="text-align:center; padding: 7px 8px; border: 1px solid #cbd5e1; font-weight: bold;">${idx + 1}</td>
          <td style="padding: 7px 8px; border: 1px solid #cbd5e1;">
            <div style="font-weight: bold; color: #0f172a;">${it.articleName || "-"}</div>
            ${it.notes ? `<div style="font-size: 11px; color: #0284c7; font-style: italic;">Note: ${it.notes}</div>` : ""}
          </td>
          <td style="padding: 7px 8px; border: 1px solid #cbd5e1; font-size: 12px; color: #475569;">${it.categoryName || "-"} (${it.unit || "pièce"})</td>
          <td style="text-align:center; padding: 7px 8px; border: 1px solid #cbd5e1; font-weight: bold;">${it.requestedQuantity || 0} ${it.unit || ""}</td>
          <td style="text-align:center; padding: 7px 8px; border: 1px solid #cbd5e1; font-weight: bold; color: ${isValidated ? "#15803d" : "#475569"};">
            ${isValidated ? (it.purchasedQuantity > 0 ? `${it.purchasedQuantity} ${it.unit || ""}` : (it.requestedQuantity || 0)) : (it.requestedQuantity || 0)}
          </td>
          <td style="text-align:right; padding: 7px 8px; border: 1px solid #cbd5e1;">
            ${(isValidated ? (Number(it.actualUnitPrice) || Number(it.estimatedUnitPrice) || 0) : (Number(it.estimatedUnitPrice) || Number(it.actualUnitPrice) || 0)).toFixed(3)}
          </td>
          <td style="text-align:right; padding: 7px 8px; border: 1px solid #cbd5e1; font-weight: bold; color: ${isValidated ? "#0f172a" : "#b45309"};">
            ${(isValidated 
              ? (Number(it.actualTotalPrice) || (Number(it.purchasedQuantity || it.requestedQuantity) * Number(it.actualUnitPrice || it.estimatedUnitPrice)) || 0)
              : (Number(it.estimatedTotalPrice) || (Number(it.requestedQuantity) * Number(it.estimatedUnitPrice || it.actualUnitPrice)) || 0)
            ).toFixed(3)} DT
          </td>
          <td style="padding: 7px 8px; border: 1px solid #cbd5e1; font-size: 12px;">${it.supplierName || reqToPrint.supplierGlobalName || "-"}</td>
        </tr>
      `
      )
      .join("");

    const statusLabel = isValidated
      ? "VALIDÉ & FACTURÉ (RÉEL)"
      : reqToPrint.status === "en_cours_achat"
      ? "EN COURS D'ACHAT (INDICATIF)"
      : reqToPrint.status === "rejete"
      ? "REJETÉ"
      : "EN ATTENTE CONTRÔLE (INDICATIF)";

    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Demande d'Achat - ${reqToPrint.reference} ${isValidated ? "(RÉEL)" : "(INDICATIF)"}</title>
          <style>
            @page { size: A4; margin: 15mm; }
            body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #0f172a; margin: 0; padding: 15px; font-size: 13px; line-height: 1.4; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 16px; }
            .logo { font-size: 22px; font-weight: 900; color: #0f172a; letter-spacing: 0.5px; }
            .logo span { color: #2563eb; }
            .title { font-size: 16px; font-weight: 800; text-transform: uppercase; color: ${isValidated ? "#1e293b" : "#b45309"}; margin-top: 4px; }
            .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 18px; }
            .meta-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; }
            .meta-row { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 12.5px; }
            .meta-label { color: #64748b; font-weight: 600; }
            .meta-val { color: #0f172a; font-weight: 800; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 18px; font-size: 12px; }
            th { background: #0f172a; color: white; padding: 8px 10px; font-weight: 700; border: 1px solid #0f172a; font-size: 11.5px; }
            .total-banner { background: ${isValidated ? "#0f172a" : "#334155"}; color: white; border-radius: 8px; padding: 12px 18px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
            .total-banner .amount { font-size: 20px; font-weight: 900; color: ${isValidated ? "#4ade80" : "#fbbf24"}; }
            .notes-box { background: #f1f5f9; border-left: 4px solid #0284c7; padding: 10px 14px; border-radius: 4px; margin-bottom: 20px; font-size: 12px; }
            .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 30px; }
            .sign-box { border: 1.5px dashed #94a3b8; border-radius: 8px; height: 100px; padding: 10px; position: relative; }
            .sign-title { font-weight: 800; font-size: 12px; color: #334155; text-transform: uppercase; }
            .footer-info { text-align: center; margin-top: 30px; font-size: 10.5px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <div class="logo">TASKO<span>POS</span></div>
              <div class="title">
                ${isValidated ? "🛒 Bon de Commande & Contrôle d'Achat (RÉEL)" : "📝 Demande d'Achat (À TITRE INDICATIF)"}
              </div>
            </div>
            <div style="text-align: right;">
              <div style="font-size: 15px; font-weight: 900; color: #0f172a;">Réf: ${reqToPrint.reference}</div>
              <div style="font-size: 12px; color: #64748b;">Émis le : ${dateFormatted}</div>
              <div style="display: inline-block; margin-top: 4px; padding: 3px 10px; background: ${isValidated ? "#dcfce7" : "#fef3c7"}; color: ${isValidated ? "#15803d" : "#b45309"}; border-radius: 12px; font-size: 11px; font-weight: 800;">
                STATUT : ${statusLabel}
              </div>
            </div>
          </div>

          ${
            isValidated
              ? `
            <div style="background: #f0fdf4; border: 1.5px solid #bbf7d0; border-left: 6px solid #16a34a; padding: 10px 14px; border-radius: 6px; margin-bottom: 16px; font-size: 12px; color: #166534;">
              <strong>✅ BON OFFICIEL VALIDÉ & CONFIRMÉ PAR LE CONTRÔLEUR :</strong>
              <div style="margin-top: 2px;">Cette demande a été validée avec les montants réels facturés. Les prix et totaux ci-dessous sont <u>DÉFINITIFS ET RÉELS</u>.</div>
            </div>
          `
              : `
            <div style="background: #fffbeb; border: 1.5px solid #fde68a; border-left: 6px solid #f59e0b; padding: 10px 14px; border-radius: 6px; margin-bottom: 16px; font-size: 12px; color: #92400e;">
              <strong>⚠️ DOCUMENT PROVISOIRE — PRIX & MONTANTS À TITRE INDICATIF :</strong>
              <div style="margin-top: 2px;">Cette demande n'a pas encore été validée par le Contrôleur. Tous les prix unitaires et montants totaux ci-dessous sont <u>À TITRE PUREMENT ESTIMATIF / INDICATIF</u> et seront ajustés lors de la validation finale.</div>
            </div>
          `
          }

          <div class="meta-grid">
            <div class="meta-card">
              <div class="meta-row">
                <span class="meta-label">📍 Emplacement :</span>
                <span class="meta-val">${reqToPrint.locationName || "-"}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">👤 Demandeur :</span>
                <span class="meta-val">${reqToPrint.requestedByName || "Commercial"}</span>
              </div>
              <div class="meta-row" style="margin: 0;">
                <span class="meta-label">🚚 Fournisseur Global :</span>
                <span class="meta-val">${reqToPrint.supplierGlobalName || "Multiple / Par article"}</span>
              </div>
            </div>

            <div class="meta-card">
              <div class="meta-row">
                <span class="meta-label">🔍 Contrôleur :</span>
                <span class="meta-val">${reqToPrint.controllerName || currentUser?.name || (isValidated ? "Service Contrôle" : "En attente")}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">📄 N° Facture / Reçu :</span>
                <span class="meta-val">${reqToPrint.invoiceNumber || (isValidated ? "-" : "En attente")}</span>
              </div>
              <div class="meta-row" style="margin: 0;">
                <span class="meta-label">💳 Mode de Paiement :</span>
                <span class="meta-val">${reqToPrint.paymentMethod || "Espèces"}</span>
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width: 30px; text-align:center;">#</th>
                <th style="text-align:left;">Désignation Article</th>
                <th style="text-align:left;">Famille / Unité</th>
                <th style="text-align:center;">Qté Demandée</th>
                <th style="text-align:center;">${isValidated ? "Qté Réelle" : "Qté Estimée"}</th>
                <th style="text-align:right;">${isValidated ? "P.U Réel (DT)" : "P.U Indicatif (DT)"}</th>
                <th style="text-align:right;">${isValidated ? "Total Réel (DT)" : "Total Indicatif (DT)"}</th>
                <th style="text-align:left;">Fournisseur</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <div class="total-banner">
            <div>
              <span style="font-size: 13px; font-weight: 700; color: #cbd5e1;">Total Articles : </span>
              <span style="font-size: 14px; font-weight: 800;">${items.length} article(s)</span>
            </div>
            <div style="text-align: right;">
              <span style="font-size: 13px; font-weight: 800; margin-right: 10px;">
                ${isValidated ? "MONTANT TOTAL RÉEL VALIDÉ :" : "MONTANT ESTIMÉ TOTAL (À TITRE INDICATIF) :"}
              </span>
              <span class="amount">${totalAmount} DT</span>
              ${!isValidated ? `<div style="font-size: 11px; color: #fde68a; font-weight: 600; margin-top: 2px;">* Montant indicatif sous réserve de facturation définitive</div>` : ""}
            </div>
          </div>

          ${
            (reqToPrint.generalNotes || reqToPrint.controllerNotes)
              ? `
            <div class="notes-box">
              ${reqToPrint.generalNotes ? `<div><strong>📝 Justification demandeur :</strong> ${reqToPrint.generalNotes}</div>` : ""}
              ${reqToPrint.controllerNotes ? `<div style="margin-top: 4px; color: #0f172a;"><strong>🔍 Observations du Contrôleur :</strong> ${reqToPrint.controllerNotes}</div>` : ""}
            </div>
          `
              : ""
          }

          <div class="signatures">
            <div class="sign-box">
              <div class="sign-title">Demandeur / Commercial Emplacement</div>
              <div style="font-size: 11px; color: #64748b; margin-top: 4px;">Signature & Date :</div>
            </div>
            <div class="sign-box">
              <div class="sign-title">Visa & Cachet Contrôleur des Achats</div>
              <div style="font-size: 11px; color: #64748b; margin-top: 4px;">${isValidated ? "Validé & Conforme :" : "Visa pour contrôle :"}</div>
            </div>
          </div>

          <div class="footer-info">
            Document généré automatiquement par TASKO POS le ${new Date().toLocaleDateString("fr-FR")} à ${new Date().toLocaleTimeString("fr-FR")} — ${isValidated ? "CONFIRMATION OFFICIELLE" : "VALEUR INDICATIVE"}
          </div>

          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=850,height=900");
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printHtml);
      printWindow.document.close();
    }
  };

  useEffect(() => {
    fetchMyRequests();
  }, []);

  // Filtrage
  const filteredRequests = requests.filter((r) => {
    const matchStatus = selectedStatus === "all" || r.status === selectedStatus;
    const matchLocation = isControllerOrAdmin
      ? (selectedLocation === "all" || r.locationName === selectedLocation)
      : true;
    const matchSearch =
      !search.trim() ||
      r.reference?.toLowerCase().includes(search.toLowerCase()) ||
      r.locationName?.toLowerCase().includes(search.toLowerCase()) ||
      r.requestedByName?.toLowerCase().includes(search.toLowerCase()) ||
      r.items?.some((it) =>
        it.articleName.toLowerCase().includes(search.toLowerCase())
      );
    return matchStatus && matchLocation && matchSearch;
  });

  // Emplacements uniques
  const uniqueLocations = Array.from(
    new Set(requests.map((r) => r.locationName).filter(Boolean))
  );

  // Statistiques synthétiques
  const countPending = requests.filter(
    (r) => r.status === "en_attente" || !r.status
  ).length;
  const countInProgress = requests.filter(
    (r) => r.status === "en_cours_achat"
  ).length;
  const countValidated = requests.filter(
    (r) => r.status === "valide_controleur"
  ).length;
  const totalApprovedAmount = requests
    .filter((r) => r.status === "valide_controleur")
    .reduce((s, r) => s + (Number(r.totalActualAmount) || 0), 0);

  const getStatusBadge = (status) => {
    switch (status) {
      case "valide_controleur":
        return {
          label: "✅ Validé & Facturé",
          bg: "#dcfce7",
          color: "#15803d",
          border: "#86efac"
        };
      case "en_cours_achat":
        return {
          label: "🛒 En cours d'achat",
          bg: "#eff6ff",
          color: "#1d4ed8",
          border: "#bfdbfe"
        };
      case "rejete":
        return {
          label: "❌ Rejeté",
          bg: "#fef2f2",
          color: "#dc2626",
          border: "#fca5a5"
        };
      case "en_attente":
      default:
        return {
          label: "⏳ En attente",
          bg: "#fffbeb",
          color: "#d97706",
          border: "#fde68a"
        };
    }
  };

  return (
    <div className="dp-card">
      {/* ── EN-TÊTE & CARTES STATISTIQUES ── */}
      <div className="dp-card-header">
        <div>
          <h3 className="dp-card-title">
            📦 Suivi de Mes Demandes d'Achats & Approvisionnements
          </h3>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "0.88rem" }}>
            Suivez en temps réel l'état d'avancement, les validations du contrôleur et les factures associées à vos demandes.
          </p>
        </div>

        {onNewDemandClick && (
          <button
            className="dp-btn dp-btn-primary"
            onClick={onNewDemandClick}
            style={{ fontWeight: "800" }}
          >
            ➕ Nouvelle Demande
          </button>
        )}
      </div>

      {/* Cartes de synthèse */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "14px",
          marginBottom: "18px"
        }}
      >
        <div
          style={{
            background: "#f8fafc",
            border: "1.5px solid #e2e8f0",
            borderRadius: "14px",
            padding: "14px 18px",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <span style={{ fontSize: "0.82rem", color: "#64748b", fontWeight: "700" }}>
            Total Demandes
          </span>
          <span style={{ fontSize: "1.6rem", fontWeight: "900", color: "#0f172a" }}>
            {requests.length}
          </span>
        </div>

        <div
          style={{
            background: "#fffbeb",
            border: "1.5px solid #fde68a",
            borderRadius: "14px",
            padding: "14px 18px",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <span style={{ fontSize: "0.82rem", color: "#b45309", fontWeight: "700" }}>
            ⏳ En Attente de Contrôle
          </span>
          <span style={{ fontSize: "1.6rem", fontWeight: "900", color: "#d97706" }}>
            {countPending}
          </span>
        </div>

        <div
          style={{
            background: "#eff6ff",
            border: "1.5px solid #bfdbfe",
            borderRadius: "14px",
            padding: "14px 18px",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <span style={{ fontSize: "0.82rem", color: "#1d4ed8", fontWeight: "700" }}>
            🛒 En Cours d'Achat
          </span>
          <span style={{ fontSize: "1.6rem", fontWeight: "900", color: "#2563eb" }}>
            {countInProgress}
          </span>
        </div>

        <div
          style={{
            background: "#f0fdf4",
            border: "1.5px solid #bbf7d0",
            borderRadius: "14px",
            padding: "14px 18px",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <span style={{ fontSize: "0.82rem", color: "#15803d", fontWeight: "700" }}>
            ✅ Validées & Confirmées
          </span>
          <span style={{ fontSize: "1.6rem", fontWeight: "900", color: "#16a34a" }}>
            {countValidated}
          </span>
        </div>

        <div
          style={{
            background: "#0f172a",
            color: "white",
            borderRadius: "14px",
            padding: "14px 18px",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 4px 14px rgba(15, 23, 42, 0.2)"
          }}
        >
          <span style={{ fontSize: "0.82rem", color: "#94a3b8", fontWeight: "700" }}>
            Total Budget Validé
          </span>
          <span style={{ fontSize: "1.5rem", fontWeight: "900", color: "#4ade80" }}>
            {totalApprovedAmount.toFixed(3)} DT
          </span>
        </div>
      </div>

      {/* ── BARRE DE RECHERCHE ET EMPLACEMENT ── */}
      <div className="dp-filters-bar mb-2">
        {isControllerOrAdmin ? (
          <select
            className="dp-filter-select"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            <option value="all">Tous les emplacements</option>
            {uniqueLocations.map((loc, idx) => (
              <option key={idx} value={loc}>
                🏪 {loc}
              </option>
            ))}
          </select>
        ) : (
          <div
            style={{
              padding: "8px 14px",
              background: "#eff6ff",
              border: "1.5px solid #bfdbfe",
              borderRadius: "10px",
              fontSize: "0.85rem",
              fontWeight: "800",
              color: "#1e40af",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            📍 Mon Emplacement : {userLocName || "Défini"}
          </div>
        )}

        <input
          type="text"
          className="dp-filter-input"
          placeholder="🔍 Réf, article, demandeur..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button
          className="dp-btn dp-btn-secondary"
          onClick={fetchMyRequests}
          title="Actualiser la liste"
        >
          🔄 Actualiser
        </button>
      </div>

      {/* ── ONGLETS DE STATUT SUR UNE SEULE LIGNE ── */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          padding: "10px 18px",
          background: "#f8fafc",
          border: "1.5px solid #e2e8f0",
          borderRadius: "12px",
          marginBottom: "16px",
          flexWrap: "nowrap",
          overflowX: "auto",
          alignItems: "center",
          scrollbarWidth: "none"
        }}
      >
        <span
          style={{
            fontSize: "0.85rem",
            fontWeight: "800",
            color: "#334155",
            whiteSpace: "nowrap",
            marginRight: "4px",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}
        >
          <span>🎯</span>
          <span>Affichage :</span>
        </span>

        <button
          type="button"
          onClick={() => setSelectedStatus("all")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "0.82rem",
            fontWeight: "800",
            whiteSpace: "nowrap",
            flexShrink: 0,
            border: "1.5px solid",
            borderColor: selectedStatus === "all" ? "#0f172a" : "#cbd5e1",
            background: selectedStatus === "all" ? "#0f172a" : "#ffffff",
            color: selectedStatus === "all" ? "#ffffff" : "#475569",
            cursor: "pointer",
            boxShadow:
              selectedStatus === "all"
                ? "0 2px 8px rgba(15, 23, 42, 0.25)"
                : "none",
            transition: "all 0.15s ease"
          }}
        >
          <span>📑 Toutes</span>
          <span
            style={{
              background: selectedStatus === "all" ? "#334155" : "#e2e8f0",
              color: selectedStatus === "all" ? "#ffffff" : "#475569",
              padding: "1px 7px",
              borderRadius: "10px",
              fontSize: "0.75rem",
              fontWeight: "900"
            }}
          >
            {requests.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setSelectedStatus("en_attente")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "0.82rem",
            fontWeight: "800",
            whiteSpace: "nowrap",
            flexShrink: 0,
            border: "1.5px solid",
            borderColor: selectedStatus === "en_attente" ? "#d97706" : "#cbd5e1",
            background: selectedStatus === "en_attente" ? "#fef3c7" : "#ffffff",
            color: selectedStatus === "en_attente" ? "#92400e" : "#475569",
            cursor: "pointer",
            boxShadow:
              selectedStatus === "en_attente"
                ? "0 2px 8px rgba(217, 119, 6, 0.25)"
                : "none",
            transition: "all 0.15s ease"
          }}
        >
          <span>⏳ À Traiter / En Attente</span>
          <span
            style={{
              background: selectedStatus === "en_attente" ? "#d97706" : "#e2e8f0",
              color: selectedStatus === "en_attente" ? "#ffffff" : "#475569",
              padding: "1px 7px",
              borderRadius: "10px",
              fontSize: "0.75rem",
              fontWeight: "900"
            }}
          >
            {countPending}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setSelectedStatus("en_cours_achat")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "0.82rem",
            fontWeight: "800",
            whiteSpace: "nowrap",
            flexShrink: 0,
            border: "1.5px solid",
            borderColor: selectedStatus === "en_cours_achat" ? "#2563eb" : "#cbd5e1",
            background: selectedStatus === "en_cours_achat" ? "#eff6ff" : "#ffffff",
            color: selectedStatus === "en_cours_achat" ? "#1d4ed8" : "#475569",
            cursor: "pointer",
            boxShadow:
              selectedStatus === "en_cours_achat"
                ? "0 2px 8px rgba(37, 99, 235, 0.25)"
                : "none",
            transition: "all 0.15s ease"
          }}
        >
          <span>🛒 En Cours d'Achat</span>
          <span
            style={{
              background: selectedStatus === "en_cours_achat" ? "#2563eb" : "#e2e8f0",
              color: selectedStatus === "en_cours_achat" ? "#ffffff" : "#475569",
              padding: "1px 7px",
              borderRadius: "10px",
              fontSize: "0.75rem",
              fontWeight: "900"
            }}
          >
            {countInProgress}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setSelectedStatus("valide_controleur")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "0.82rem",
            fontWeight: "800",
            whiteSpace: "nowrap",
            flexShrink: 0,
            border: "1.5px solid",
            borderColor:
              selectedStatus === "valide_controleur" ? "#16a34a" : "#cbd5e1",
            background:
              selectedStatus === "valide_controleur" ? "#f0fdf4" : "#ffffff",
            color: selectedStatus === "valide_controleur" ? "#15803d" : "#475569",
            cursor: "pointer",
            boxShadow:
              selectedStatus === "valide_controleur"
                ? "0 2px 8px rgba(22, 163, 74, 0.25)"
                : "none",
            transition: "all 0.15s ease"
          }}
        >
          <span>✅ Validées & Facturées</span>
          <span
            style={{
              background:
                selectedStatus === "valide_controleur" ? "#16a34a" : "#e2e8f0",
              color:
                selectedStatus === "valide_controleur" ? "#ffffff" : "#475569",
              padding: "1px 7px",
              borderRadius: "10px",
              fontSize: "0.75rem",
              fontWeight: "900"
            }}
          >
            {countValidated}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setSelectedStatus("rejete")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "0.82rem",
            fontWeight: "800",
            whiteSpace: "nowrap",
            flexShrink: 0,
            border: "1.5px solid",
            borderColor: selectedStatus === "rejete" ? "#dc2626" : "#cbd5e1",
            background: selectedStatus === "rejete" ? "#fee2e2" : "#ffffff",
            color: selectedStatus === "rejete" ? "#b91c1c" : "#475569",
            cursor: "pointer",
            boxShadow:
              selectedStatus === "rejete"
                ? "0 2px 8px rgba(220, 38, 38, 0.25)"
                : "none",
            transition: "all 0.15s ease"
          }}
        >
          <span>❌ Rejetées</span>
          <span
            style={{
              background: selectedStatus === "rejete" ? "#dc2626" : "#e2e8f0",
              color: selectedStatus === "rejete" ? "#ffffff" : "#475569",
              padding: "1px 7px",
              borderRadius: "10px",
              fontSize: "0.75rem",
              fontWeight: "900"
            }}
          >
            {requests.filter((r) => r.status === "rejete").length}
          </span>
        </button>
      </div>

      {/* ── TABLEAU DES DEMANDES AVEC SUIVI ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
          ⏳ Chargement de l'historique de vos demandes...
        </div>
      ) : filteredRequests.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "50px 20px",
            background: "#f8fafc",
            borderRadius: "14px",
            border: "2px dashed #cbd5e1",
            color: "#64748b"
          }}
        >
          <div style={{ fontSize: "2.5rem", marginBottom: "8px" }}>📭</div>
          <div style={{ fontWeight: "800", fontSize: "1.1rem", color: "#1e293b" }}>
            Aucune demande d'achat trouvée
          </div>
          <p style={{ margin: "4px 0 14px", fontSize: "0.88rem" }}>
            Les nouvelles demandes d'achats transmises apparaîtront automatiquement ici avec leur statut en direct.
          </p>
        </div>
      ) : (
        <div className="dp-table-container">
          <table className="dp-table">
            <thead>
              <tr>
                <th>Référence & Date</th>
                <th>Emplacement</th>
                <th>Demandeur</th>
                <th>Articles Demandés</th>
                <th style={{ textAlign: "right" }}>Montant Estimé</th>
                <th style={{ textAlign: "right" }}>Montant Réel Approuvé</th>
                <th style={{ textAlign: "center" }}>Statut d'Avancement</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((req) => {
                const badge = getStatusBadge(req.status);
                const dateFormatted = req.createdAt
                  ? new Date(req.createdAt).toLocaleDateString("fr-FR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })
                  : "-";

                return (
                  <tr key={req._id}>
                    <td>
                      <div style={{ fontWeight: "800", color: "#0f172a" }}>
                        {req.reference}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                        📅 {dateFormatted}
                      </div>
                    </td>

                    <td>
                      <span
                        style={{
                          fontWeight: "700",
                          color: "#1e293b",
                          fontSize: "0.88rem"
                        }}
                      >
                        🏪 {req.locationName}
                      </span>
                    </td>

                    <td>
                      <div style={{ fontWeight: "700", color: "#334155" }}>
                        👤 {req.requestedByName || "Commercial"}
                      </div>
                    </td>

                    <td>
                      <span
                        style={{
                          background: "#f1f5f9",
                          padding: "4px 10px",
                          borderRadius: "8px",
                          fontSize: "0.82rem",
                          fontWeight: "700",
                          color: "#334155"
                        }}
                      >
                        📦 {req.items?.length || 0} article(s)
                      </span>
                    </td>

                    <td style={{ textAlign: "right", fontWeight: "700", color: "#475569" }}>
                      {(Number(req.totalEstimatedAmount) || 0).toFixed(3)} DT
                    </td>

                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: "900",
                        color:
                          req.status === "valide_controleur"
                            ? "#15803d"
                            : "#94a3b8"
                      }}
                    >
                      {req.status === "valide_controleur"
                        ? `${(Number(req.totalActualAmount) || 0).toFixed(3)} DT`
                        : "En cours..."}
                    </td>

                    <td style={{ textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "0.78rem",
                          fontWeight: "800",
                          background: badge.bg,
                          color: badge.color,
                          border: `1px solid ${badge.border}`
                        }}
                      >
                        {badge.label}
                      </span>
                      {req.invoiceNumber && (
                        <div
                          style={{
                            fontSize: "0.72rem",
                            color: "#64748b",
                            fontWeight: "700",
                            marginTop: "2px"
                          }}
                        >
                          📄 Facture : {req.invoiceNumber}
                        </div>
                      )}
                    </td>

                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "inline-flex", gap: "6px", alignItems: "center" }}>
                        <button
                          className="dp-btn dp-btn-primary"
                          style={{
                            padding: "5px 12px",
                            fontSize: "0.78rem",
                            fontWeight: "800",
                            whiteSpace: "nowrap"
                          }}
                          onClick={() => setSelectedRequest(req)}
                        >
                          👁️ Détails
                        </button>
                        <button
                          className="dp-btn dp-btn-secondary"
                          style={{
                            padding: "5px 10px",
                            fontSize: "0.78rem",
                            fontWeight: "750",
                            whiteSpace: "nowrap"
                          }}
                          onClick={() => handlePrintRequest(req)}
                          title="Imprimer le bon d'achat"
                        >
                          🖨️ Imprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── MODALE DÉTAILS DE LA DEMANDE ── */}
      <Modal
        show={!!selectedRequest}
        onHide={() => setSelectedRequest(null)}
        size="lg"
        centered
        className="dp-custom-modal-root"
      >
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            color: "white"
          }}
        >
          <Modal.Title
            style={{
              color: "#ffffff",
              fontWeight: "800",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "1.1rem"
            }}
          >
            <span>📋</span>
            <span>
              Suivi Détail : {selectedRequest?.reference} — {selectedRequest?.locationName}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "20px" }}>
          {/* Bannière état */}
          <div
            style={{
              background: "#f8fafc",
              border: "1.5px solid #e2e8f0",
              borderRadius: "12px",
              padding: "14px 18px",
              marginBottom: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px"
            }}
          >
            <div>
              <div style={{ fontSize: "0.82rem", color: "#64748b", fontWeight: "700" }}>
                RÉFÉRENCE & DATE :
              </div>
              <div style={{ fontSize: "1.15rem", fontWeight: "900", color: "#0f172a" }}>
                {selectedRequest?.reference}
              </div>
              <div style={{ fontSize: "0.85rem", color: "#475569" }}>
                📍 Emplacement : <strong>{selectedRequest?.locationName}</strong> | Demandé par : <strong>{selectedRequest?.requestedByName}</strong>
              </div>
            </div>

            <div style={{ textAlign: "right" }}>
              {selectedRequest && (
                <span
                  style={{
                    display: "inline-block",
                    padding: "6px 14px",
                    borderRadius: "20px",
                    fontSize: "0.85rem",
                    fontWeight: "800",
                    background: getStatusBadge(selectedRequest.status).bg,
                    color: getStatusBadge(selectedRequest.status).color,
                    border: `1px solid ${getStatusBadge(selectedRequest.status).border}`
                  }}
                >
                  {getStatusBadge(selectedRequest.status).label}
                </span>
              )}
              {selectedRequest?.controllerName && (
                <div style={{ fontSize: "0.8rem", color: "#166534", fontWeight: "700", marginTop: "4px" }}>
                  Contrôlé par : {selectedRequest.controllerName}
                </div>
              )}
            </div>
          </div>

          {/* Tableau des articles */}
          <h6 style={{ fontWeight: "800", color: "#0f172a", marginBottom: "8px" }}>
            🛒 Détail des Articles :
          </h6>

          <div
            style={{
              border: "1.5px solid #e2e8f0",
              borderRadius: "10px",
              overflow: "hidden",
              marginBottom: "14px"
            }}
          >
            <table className="dp-table" style={{ margin: 0 }}>
              <thead style={{ background: "#f1f5f9" }}>
                <tr>
                  <th>Article</th>
                  <th>Famille</th>
                  <th style={{ textAlign: "center" }}>Qté Demandée</th>
                  <th style={{ textAlign: "center" }}>Qté Réelle Achetée</th>
                  <th style={{ textAlign: "right" }}>Prix Unitaire (DT)</th>
                  <th style={{ textAlign: "right" }}>Total (DT)</th>
                  <th>Fournisseur</th>
                </tr>
              </thead>
              <tbody>
                {(selectedRequest?.items || []).map((it, idx) => (
                  <tr key={idx}>
                    <td style={{ fontWeight: "800", color: "#0f172a" }}>
                      {it.articleName}
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: "0.8rem",
                          padding: "2px 6px",
                          background: "#f1f5f9",
                          borderRadius: "4px",
                          fontWeight: "600"
                        }}
                      >
                        {it.categoryName}
                      </span>
                    </td>
                    <td style={{ textAlign: "center", fontWeight: "700" }}>
                      {it.requestedQuantity} {it.unit}
                    </td>
                    <td style={{ textAlign: "center", fontWeight: "800", color: "#15803d" }}>
                      {it.purchasedQuantity > 0 ? `${it.purchasedQuantity} ${it.unit}` : "-"}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: "600" }}>
                      {(Number(it.actualUnitPrice) || Number(it.estimatedUnitPrice) || 0).toFixed(3)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: "800", color: "#0f172a" }}>
                      {(
                        Number(it.actualTotalPrice) ||
                        Number(it.estimatedTotalPrice) ||
                        0
                      ).toFixed(3)}{" "}
                      DT
                    </td>
                    <td>
                      <span style={{ fontSize: "0.82rem", fontWeight: "700", color: "#1e293b" }}>
                        {it.supplierName || selectedRequest?.supplierGlobalName || "-"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot style={{ background: "#f8fafc", borderTop: "2px solid #cbd5e1" }}>
                <tr>
                  <td
                    colSpan="5"
                    style={{ textAlign: "right", fontWeight: "800", color: "#0f172a" }}
                  >
                    MONTANT TOTAL :
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: "900",
                      fontSize: "1.1rem",
                      color: "#15803d"
                    }}
                  >
                    {(
                      Number(selectedRequest?.totalActualAmount) ||
                      Number(selectedRequest?.totalEstimatedAmount) ||
                      0
                    ).toFixed(3)}{" "}
                    DT
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Remarques du contrôleur ou du demandeur */}
          {(selectedRequest?.controllerNotes || selectedRequest?.generalNotes) && (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "10px 14px",
                fontSize: "0.85rem",
                color: "#334155"
              }}
            >
              {selectedRequest?.generalNotes && (
                <div style={{ marginBottom: "4px" }}>
                  <strong>📝 Note du demandeur :</strong> {selectedRequest.generalNotes}
                </div>
              )}
              {selectedRequest?.controllerNotes && (
                <div style={{ color: "#166534" }}>
                  <strong>🔍 Visa & Observations du Contrôleur :</strong>{" "}
                  {selectedRequest.controllerNotes}
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{
            background: "#f8fafc",
            padding: "12px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Button
            variant="outline-secondary"
            style={{
              fontWeight: "800",
              border: "1.5px solid #cbd5e1",
              background: "#ffffff",
              color: "#0f172a"
            }}
            onClick={() => handlePrintRequest(selectedRequest)}
            title="Imprimer le bon de demande et contrôle d'achat"
          >
            🖨️ Imprimer la Demande
          </Button>

          <Button
            variant="secondary"
            onClick={() => setSelectedRequest(null)}
            style={{ fontWeight: "700" }}
          >
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SuiviDemandesAchats;
